import { useEffect, useState } from "react";
import axios from "axios";
import { useQueryClient } from "@tanstack/react-query";
import { Megaphone } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";
import { API } from "@/lib/api";
import { AD_DEFAULTS } from "@/config/ads";

const NumField = ({ id, label, hint, value, onChange, max }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</span>
    <input
      data-testid={`ad-setting-${id}`}
      type="number"
      min={0}
      max={max}
      value={value}
      onChange={(e) => onChange(e.target.value === "" ? "" : Math.max(0, Math.min(max, Number(e.target.value) || 0)))}
      className="mt-1.5 w-full border border-white/10 bg-[#111] px-3 py-2 text-sm text-white focus:border-amber focus:outline-none"
    />
    <span className="mt-1 block font-mono text-[10px] text-white/35">{value === "" ? "Enter a number (0 = off)" : value === 0 ? "Off" : hint(value)}</span>
  </label>
);

// Admin: how often ad units render site-wide (Kids hub, TV Mode and admin are always ad-free).
export const AdFrequencyPanel = ({ headers }) => {
  const qc = useQueryClient();
  const [s, setS] = useState(AD_DEFAULTS);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    axios.get(`${API}/settings/ads`).then(({ data }) => setS(data)).catch(() => {});
  }, []);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...s, banner_every_rows: Number(s.banner_every_rows) || 0, native_every_cards: Number(s.native_every_cards) || 0 };
      const { data } = await axios.put(`${API}/admin/settings/ads`, body, { headers, withCredentials: true });
      setS(data);
      qc.invalidateQueries({ queryKey: ["ad-settings"] });
      toast.success("Ad frequency saved — live for new page loads");
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not save");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section data-testid="ad-frequency-panel" className="border border-white/10 bg-[#0A0A0A] p-6 sm:p-8">
      <div className="mb-5 flex items-center gap-3">
        <Megaphone size={22} weight="duotone" className="text-amber" />
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">Ad Frequency</h2>
      </div>
      <div className="grid gap-5 sm:grid-cols-3">
        <NumField id="banner-every-rows" label="Homepage banner every N rows" max={20} value={s.banner_every_rows}
          onChange={(v) => setS({ ...s, banner_every_rows: v })} hint={(v) => `A banner after every ${v} genre row${v === 1 ? "" : "s"}`} />
        <NumField id="native-every-cards" label="Sponsored card every N titles" max={50} value={s.native_every_cards}
          onChange={(v) => setS({ ...s, native_every_cards: v })} hint={(v) => `One sponsored card per ${v} grid title${v === 1 ? "" : "s"}`} />
        <label className="flex items-center gap-3 self-center">
          <input data-testid="ad-setting-player-banner" type="checkbox" checked={s.player_banner}
            onChange={(e) => setS({ ...s, player_banner: e.target.checked })} className="h-4 w-4 accent-amber" />
          <span className="text-sm text-white/75">Banner below the video player</span>
        </label>
      </div>
      <div className="mt-6 flex items-center gap-4">
        <button data-testid="ad-settings-save" onClick={save} disabled={busy}
          className="border border-amber/60 px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.18em] text-amber transition-colors duration-300 hover:bg-amber hover:text-black disabled:opacity-40">
          {busy ? "Saving…" : "Save ad frequency"}
        </button>
        <span className="font-mono text-[10px] text-white/35">Set 0 to switch a unit off. Kids hub, TV Mode and admin never show ads.</span>
      </div>
    </section>
  );
};
